/**
 * AI Classification Flow Component
 * Progressive 4-phase AI classification UI with animated phase cards.
 * Based on specification from /app/memory/ai-classification-ui.jsx
 */
import React, { useState, useEffect } from 'react';
import { Search, Sparkles, Scale, FileText, Filter, Check, Loader2, ChevronRight, Clock, AlertCircle, X } from 'lucide-react';

const phaseConfig = {
  phase1: {
    id: 1,
    key: 'phase1',
    title: 'Understanding the Part',
    subtitle: 'Extracting functional essence',
    icon: Search,
    iconBg: 'from-blue-600 to-blue-700',
    questionKey: 'What does this part do?'
  },
  phase2_5: {
    id: 2,
    key: 'phase2_5',
    title: 'Pre-Filter Candidates',
    subtitle: 'Finding candidate subgroups (no AI)',
    icon: Filter,
    iconBg: 'from-purple-600 to-purple-700',
    questionKey: 'Which existing sub-groups might match?'
  },
  phase2: {
    id: 3,
    key: 'phase2',
    title: 'Semantic Search',
    subtitle: 'Evaluating candidates for functional match',
    icon: Sparkles,
    iconBg: 'from-indigo-600 to-indigo-700',
    questionKey: 'Does a functionally equivalent sub-group exist?'
  },
  phase3: {
    id: 4,
    key: 'phase3',
    title: 'Identifying Product Family',
    subtitle: 'Finding the right shelf label',
    icon: Scale,
    iconBg: 'from-amber-600 to-amber-700',
    questionKey: 'What product family does this belong to?'
  },
  phase4: {
    id: 5,
    key: 'phase4',
    title: 'Naming & Metadata',
    subtitle: 'Creating a future-proof sub-group',
    icon: FileText,
    iconBg: 'from-emerald-600 to-emerald-700',
    questionKey: 'What name will accommodate future parts?'
  }
};

const getVerdictStyle = (verdict) => {
  switch(verdict?.toLowerCase()) {
    case 'too_narrow': return { bg: 'bg-red-900/30', border: 'border-red-500/50', text: 'text-red-400', icon: '✗' };
    case 'narrow': return { bg: 'bg-yellow-900/30', border: 'border-yellow-500/50', text: 'text-yellow-400', icon: '~' };
    case 'optimal': return { bg: 'bg-emerald-900/30', border: 'border-emerald-500/50', text: 'text-emerald-400', icon: '✓' };
    case 'broad': return { bg: 'bg-yellow-900/30', border: 'border-yellow-500/50', text: 'text-yellow-400', icon: '~' };
    case 'too_broad': return { bg: 'bg-red-900/30', border: 'border-red-500/50', text: 'text-red-400', icon: '✗' };
    default: return { bg: 'bg-slate-800', border: 'border-slate-600', text: 'text-slate-400', icon: '' };
  }
};

// Phase 1 Content: Understanding
const Phase1Content = ({ output }) => {
  if (!output) return null;
  
  const findings = [
    { label: 'Primary Function', value: output.primaryFunction },
    { label: 'Category', value: output.category },
    { label: 'Type', value: output.type },
    { label: 'Specifics', value: output.specifics?.join(', ') || 'N/A' }
  ];
  
  return (
    <div className="space-y-3">
      {findings.map((finding, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
          <div>
            <span className="text-slate-500 text-sm">{finding.label}:</span>
            <span className="text-white text-sm ml-2">{finding.value}</span>
          </div>
        </div>
      ))}
      <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-xl">
        <p className="text-blue-300 text-sm">
          <span className="font-medium">Functional Essence:</span> {output.functionalEssence}
        </p>
      </div>
      {output.searchTerms?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {output.searchTerms.slice(0, 5).map((term, i) => (
            <span key={i} className="px-2 py-1 bg-slate-700/50 rounded-lg text-xs text-slate-300 border border-slate-600/50">
              {term}
            </span>
          ))}
          {output.searchTerms.length > 5 && (
            <span className="px-2 py-1 text-xs text-slate-500">+{output.searchTerms.length - 5} more</span>
          )}
        </div>
      )}
    </div>
  );
};

// Phase 2.5 Content: Pre-Filter
const Phase25Content = ({ output }) => {
  if (!output) return null;
  
  return (
    <div>
      <div className="p-3 bg-purple-900/20 border border-purple-500/30 rounded-xl">
        <p className="text-purple-300 text-sm">
          <span className="font-medium">Result:</span> Found {output.candidatesFound || 0} candidates from {output.subgroupsEvaluated || 0} subgroups
        </p>
      </div>
    </div>
  );
};

// Phase 2 Content: Semantic Search
const Phase2Content = ({ output }) => {
  if (!output) return null;
  
  const matchFound = output.matchFound;
  
  return (
    <div>
      {output.evaluationLog?.length > 0 && (
        <div className="mb-4 space-y-2 max-h-32 overflow-y-auto">
          {output.evaluationLog.slice(0, 4).map((entry, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {entry.isMatch ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : (
                <X className="w-4 h-4 text-slate-500" />
              )}
              <span className={entry.isMatch ? 'text-emerald-300' : 'text-slate-400'}>{entry.subGroupName}</span>
              <span className="text-slate-600 text-xs">— {entry.reason}</span>
            </div>
          ))}
        </div>
      )}
      <div className={`p-3 rounded-xl border ${matchFound ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-amber-900/20 border-amber-500/30'}`}>
        <p className={`text-sm ${matchFound ? 'text-emerald-300' : 'text-amber-300'}`}>
          <span className="font-medium">Result:</span> {output.matchReasoning || (matchFound ? 'Match found!' : 'No existing match — will create new subgroup')}
        </p>
      </div>
    </div>
  );
};

// Phase 3 Content: Product Family (Shelf Label Test)
const Phase3Content = ({ output }) => {
  if (!output) return null;
  
  // Handle new Phase 3 structure with analysis, shelfLabelOptions, recommendation
  const { analysis, shelfLabelOptions, recommendation } = output;
  
  // Fallback for legacy structure (evaluations-based)
  if (output.evaluations && !shelfLabelOptions) {
    return (
      <div className="space-y-3">
        {output.evaluations?.map((evaluation, i) => {
          const style = getVerdictStyle(evaluation.verdict);
          return (
            <div 
              key={i} 
              className={`p-4 rounded-xl border ${style.bg} ${style.border} transition-all`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">{evaluation.level}</span>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400 text-sm">{evaluation.estimatedVariations}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${style.bg} ${style.text} border ${style.border}`}>
                    {evaluation.capacityPercentage} of capacity
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-lg ${style.text}`}>{style.icon}</span>
                <span className="text-slate-400 text-sm">{evaluation.reasoning}</span>
              </div>
            </div>
          );
        })}
        <div className="mt-4 p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
          <p className="text-emerald-300 text-sm">
            <span className="font-medium">Optimal Level:</span> {output.optimalLevel}
          </p>
          <p className="text-emerald-400/70 text-xs mt-1">{output.optimalReasoning}</p>
        </div>
      </div>
    );
  }
  
  // New Phase 3 structure: Shelf Label Test
  return (
    <div className="space-y-4">
      {/* Analysis Summary - What it does */}
      {analysis && (
        <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl">
          <p className="text-blue-300 text-sm font-medium mb-2">What it does:</p>
          <p className="text-white text-sm">{analysis.whatDoesItDo}</p>
          <div className="mt-3 pt-3 border-t border-blue-500/20">
            <p className="text-blue-400/70 text-xs mb-1">Primary Function:</p>
            <p className="text-slate-300 text-sm">{analysis.primaryFunction}</p>
          </div>
          {analysis.secondaryFunctions?.length > 0 && (
            <div className="mt-2">
              <p className="text-blue-400/70 text-xs mb-1">Secondary Functions:</p>
              <div className="flex flex-wrap gap-1">
                {analysis.secondaryFunctions.map((func, i) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-900/30 rounded text-xs text-blue-300">
                    {func}
                  </span>
                ))}
              </div>
            </div>
          )}
          {analysis.whoSearchesForThis && (
            <div className="mt-2">
              <p className="text-blue-400/70 text-xs">
                <span className="text-slate-500">Search terms:</span> {analysis.whoSearchesForThis}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Shelf Label Options - 3 options */}
      {shelfLabelOptions?.length > 0 && (
        <div className="space-y-2">
          <p className="text-slate-400 text-sm font-medium mb-2">Product Family Options:</p>
          {shelfLabelOptions.map((option, i) => {
            const isOptimal = recommendation && option.label === recommendation.shelfLabel;
            const optionStyle = isOptimal 
              ? { bg: 'bg-emerald-900/30', border: 'border-emerald-500/50', text: 'text-emerald-400', icon: '✓' }
              : option.wouldHumanUseThis
                ? { bg: 'bg-yellow-900/30', border: 'border-yellow-500/50', text: 'text-yellow-400', icon: '~' }
                : { bg: 'bg-slate-800/50', border: 'border-slate-600/50', text: 'text-slate-400', icon: '✗' };
            
            return (
              <div 
                key={i} 
                className={`p-3 rounded-xl border ${optionStyle.bg} ${optionStyle.border} transition-all`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-lg ${optionStyle.text}`}>{optionStyle.icon}</span>
                    <span className="text-white font-medium">{option.label}</span>
                    {isOptimal && (
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30">
                        RECOMMENDED
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-slate-400 text-sm mt-1 ml-7">{option.reasoning}</p>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Recommendation - What belongs/doesn't belong */}
      {recommendation && (
        <div className="mt-4 p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
          <p className="text-emerald-300 text-sm font-medium mb-2">
            Recommended: {recommendation.shelfLabel}
          </p>
          <p className="text-emerald-400/70 text-xs mb-3">{recommendation.reasoning}</p>
          
          {/* What belongs here */}
          {recommendation.whatBelongsHere?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-500/20">
              <p className="text-emerald-400/70 text-xs mb-2">This sub-group includes:</p>
              <div className="flex flex-wrap gap-1">
                {recommendation.whatBelongsHere.map((item, i) => (
                  <span key={i} className="px-2 py-1 bg-emerald-900/30 rounded text-xs text-emerald-300 border border-emerald-500/20">
                    ✓ {item}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* What doesn't belong */}
          {recommendation.whatDoesNotBelong?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-500/20">
              <p className="text-red-400/70 text-xs mb-2">Does not include:</p>
              <div className="flex flex-wrap gap-1">
                {recommendation.whatDoesNotBelong.map((item, i) => (
                  <span key={i} className="px-2 py-1 bg-red-900/20 rounded text-xs text-red-300 border border-red-500/20">
                    ✗ {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Phase 4 Content: Naming & Metadata
const Phase4Content = ({ output }) => {
  if (!output) return null;
  
  return (
    <div>
      {output.accommodates?.length > 0 && (
        <>
          <p className="text-slate-400 text-sm mb-3">Future parts this sub-group will accommodate:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {output.accommodates.slice(0, 6).map((item, i) => (
              <span key={i} className="px-3 py-1.5 bg-emerald-900/20 rounded-lg text-sm text-emerald-300 border border-emerald-500/30">
                ✓ {item}
              </span>
            ))}
          </div>
        </>
      )}
      <div className="p-4 bg-gradient-to-r from-blue-900/30 to-emerald-900/30 border border-blue-500/30 rounded-xl">
        <p className="text-slate-400 text-sm mb-2">Recommended Classification</p>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-slate-800 rounded-lg border border-slate-600">
            <p className="text-slate-500 text-xs">Main Group</p>
            <p className="text-white font-medium">{output.mainGroupRecommendation?.id} - {output.mainGroupRecommendation?.name}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-600" />
          <div className="px-4 py-2 bg-blue-900/30 rounded-lg border border-blue-500/50">
            <p className="text-blue-400 text-xs">Sub-Group (New)</p>
            <p className="text-white font-medium">{output.name}</p>
          </div>
        </div>
      </div>
      {output.synonyms?.length > 0 && (
        <div className="mt-4">
          <p className="text-slate-500 text-xs mb-2">AI-generated synonyms:</p>
          <div className="flex flex-wrap gap-2">
            {output.synonyms.slice(0, 4).map((syn, i) => (
              <span key={i} className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300">{syn}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Single Phase Card Component
const PhaseCard = ({ phase, config, isVisible, isActive, isComplete, isSkipped }) => {
  const Icon = config.icon;
  const durationSecs = phase?.durationMs ? (phase.durationMs / 1000).toFixed(1) : null;
  
  const getPhaseContent = () => {
    if (!phase?.output) return null;
    
    switch (config.key) {
      case 'phase1': return <Phase1Content output={phase.output} />;
      case 'phase2_5': return <Phase25Content output={phase.output} />;
      case 'phase2': return <Phase2Content output={phase.output} />;
      case 'phase3': return <Phase3Content output={phase.output} />;
      case 'phase4': return <Phase4Content output={phase.output} />;
      default: return null;
    }
  };
  
  if (!isVisible) return null;
  
  return (
    <div 
      className={`transition-all duration-500 ${
        isComplete || isActive
          ? 'opacity-100 translate-y-0' 
          : isSkipped ? 'opacity-40 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className={`bg-slate-800/30 border rounded-2xl overflow-hidden backdrop-blur-sm ${
        isActive ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-slate-700/50'
      }`}>
        {/* Phase Header */}
        <div className="flex items-center gap-4 p-5 border-b border-slate-700/30">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.iconBg} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-medium">{config.title}</h3>
            <p className="text-slate-500 text-sm">{config.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            {durationSecs && (
              <span className="text-slate-500 text-sm">{durationSecs}s</span>
            )}
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
              isComplete ? 'bg-emerald-500/20' : 
              isActive ? 'bg-blue-500/20' : 
              isSkipped ? 'bg-slate-600/20' : 'bg-slate-700/50'
            }`}>
              {isComplete ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : isActive ? (
                <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
              ) : isSkipped ? (
                <span className="text-slate-500 text-xs">—</span>
              ) : (
                <Clock className="w-4 h-4 text-slate-500" />
              )}
            </div>
          </div>
        </div>

        {/* Phase Content */}
        {(isComplete || isActive) && !isSkipped && (
          <div className="p-5">
            {isActive && !phase?.output ? (
              <div className="flex items-center gap-3 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              <>
                <p className="text-blue-400 text-sm mb-4 italic">&ldquo;{config.questionKey}&rdquo;</p>
                {getPhaseContent()}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main AI Classification Flow Component
const AIClassificationFlow = ({
  partData,
  phases = [],
  currentPhase = null,
  isComplete = false,
  isClassifying = false,
  totalDurationMs = 0,
  totalApiCalls = 0,
  classification = null,
  onAccept,
  onAdjust,
  error = null
}) => {
  const [visiblePhases, setVisiblePhases] = useState([]);
  
  // Build phase lookup
  const phaseLookup = {};
  phases.forEach(p => {
    phaseLookup[p.phase] = p;
  });
  
  // Determine if phases 3-4 should be shown (only if no match found)
  const phase2Data = phaseLookup['phase2'];
  const hasMatch = phase2Data?.output?.matchFound;
  
  // Progressive reveal animation
  useEffect(() => {
    if (isClassifying || phases.length > 0) {
      const allPhaseKeys = ['phase1', 'phase2_5', 'phase2', 'phase3', 'phase4'];
      let index = 0;
      
      const revealNext = () => {
        if (index < allPhaseKeys.length) {
          setVisiblePhases(prev => [...prev, allPhaseKeys[index]]);
          index++;
          
          // Check if this phase should be skipped (phases 3-4 if match found)
          const nextKey = allPhaseKeys[index];
          if (hasMatch && (nextKey === 'phase3' || nextKey === 'phase4')) {
            return; // Don't reveal phases 3-4 if match found
          }
          
          if (index < allPhaseKeys.length) {
            setTimeout(revealNext, 300);
          }
        }
      };
      
      if (visiblePhases.length === 0) {
        revealNext();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClassifying, phases.length, hasMatch]);
  
  const allPhaseKeys = ['phase1', 'phase2_5', 'phase2', 'phase3', 'phase4'];
  
  return (
    <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <span className="text-white font-bold">2</span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white tracking-tight">AI Classification</h2>
            <p className="text-slate-400 text-sm">Step 2 of 3 • Analyzing part and finding classification</p>
          </div>
        </div>
        {isClassifying && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            Processing with AI
          </div>
        )}
        {isComplete && (
          <div className="text-sm text-slate-500">
            {totalApiCalls} AI calls • {(totalDurationMs / 1000).toFixed(1)}s total
          </div>
        )}
      </div>

      {/* Part Summary Card */}
      {partData && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-6 backdrop-blur-sm">
          <div>
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Classifying Part</p>
            <h3 className="text-white font-medium mb-2 line-clamp-2">{partData.description}</h3>
            <div className="flex gap-4 text-sm">
              <span className="text-slate-400">
                <span className="text-slate-500">Manufacturer:</span> {partData.manufacturer}
              </span>
              <span className="text-slate-400">
                <span className="text-slate-500">MPN:</span> <span className="font-mono">{partData.mpn}</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-xl mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-300 font-medium">Classification Error</p>
            <p className="text-red-400/70 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Phase Cards */}
      <div className="space-y-4">
        {allPhaseKeys.map((phaseKey) => {
          const config = phaseConfig[phaseKey];
          const phaseData = phaseLookup[phaseKey];
          const isActive = currentPhase === phaseKey;
          const isPhaseComplete = phaseData?.status === 'complete';
          const isSkipped = (phaseKey === 'phase3' || phaseKey === 'phase4') && hasMatch;
          const isVisible = visiblePhases.includes(phaseKey) || isPhaseComplete;
          
          // Don't render skipped phases
          if (isSkipped && !isPhaseComplete) return null;
          
          return (
            <PhaseCard
              key={phaseKey}
              phase={phaseData}
              config={config}
              isVisible={isVisible}
              isActive={isActive}
              isComplete={isPhaseComplete}
              isSkipped={isSkipped}
            />
          );
        })}
      </div>

      {/* Classification Result */}
      {isComplete && classification && (
        <div className="mt-6 p-5 bg-slate-800/50 border border-slate-700/50 rounded-2xl backdrop-blur-sm animate-fade-in">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider">AI Recommendation</p>
                <p className="text-white font-semibold">
                  {classification.primary_suggestion?.main_group_code} - {classification.primary_suggestion?.main_group_name || 'Main Group'}
                </p>
                <p className="text-slate-300">
                  {classification.primary_suggestion?.subgroup_name}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                classification.confidence_level === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                classification.confidence_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                'bg-slate-500/20 text-slate-400 border border-slate-500/30'
              }`}>
                {classification.confidence_level}
              </span>
              <p className="text-slate-500 text-sm mt-1">{Math.round(classification.confidence * 100)}% confidence</p>
            </div>
          </div>
          
          {/* New Subgroup Indicator */}
          {!classification.primary_suggestion?.subgroup_exists && (
            <div className="p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl mb-4">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <span className="text-lg">+</span>
                <span className="font-medium">New subgroup will be created</span>
              </div>
              <p className="text-emerald-300/70 text-sm">
                The subgroup &ldquo;{classification.primary_suggestion?.subgroup_name}&rdquo; does not exist yet and will be automatically created.
              </p>
              {classification.phase4_metadata?.synonyms?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-500/20">
                  <p className="text-emerald-400/70 text-xs mb-2">AI-generated metadata:</p>
                  <div className="flex flex-wrap gap-2">
                    {classification.phase4_metadata.synonyms.slice(0, 4).map((syn, i) => (
                      <span key={i} className="px-2 py-1 bg-emerald-900/30 rounded text-xs text-emerald-300">{syn}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-700/50">
            <div>
              <p className="text-emerald-400 text-sm font-medium flex items-center gap-2">
                <Check className="w-4 h-4" />
                Classification Complete
              </p>
              <p className="text-slate-500 text-sm">Review the reasoning above and confirm or adjust</p>
            </div>
            <div className="flex gap-3">
              {onAdjust && (
                <button 
                  onClick={onAdjust}
                  className="px-5 py-2.5 text-slate-300 hover:text-white transition-colors"
                >
                  Adjust Manually
                </button>
              )}
              {onAccept && (
                <button 
                  onClick={onAccept}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl transition-colors"
                >
                  Accept & Continue
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIClassificationFlow;
